import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';

import { generalConstants, iconConstants, routesConstants } from '@/constants';
import Icons from '@/icons';
import { authHelpers, generalHelpers } from '@/helpers';
import { Button, DatePickerInput, Dropdown, ImagePicker, Input, Textarea } from '@/kaya';
import OtpScreen from '../otpScreen/OtpScreen';
import { authTypes } from '@/types';
import { useArtistStore } from '@/store';
import { userActions } from '@/actions';
import logo from '@/assets/upperLogin-image.png';

import styles from './PersonalInfo.module.scss';
import DropdownWithInput from '@/component/dropdownWithInput/DropdownWithInput';

const PersonalInfo = () => {
  const { setArtist, artist } = useArtistStore();

  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<authTypes.PersonalInfo>(
    authTypes.INITIAL_PERSONAL_INFO_VALUES,
  );

  // const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [visible, setVisible] = React.useState(false);
  const [token, setToken] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [isLoading2, setIsLoading2] = React.useState(false);
  const [err, setErr] = React.useState({
    first_name: '',
    last_name: '',
    national_identity_no: '',
    phone: '',
    stage_name: '',
    description: '',
    birthday: '',
    country: '',
    username: '',
  });

  const handleSubmit = async (otp: string) => {
    setIsLoading2(true);
    const isSuccess = await userActions.verifyOtpAction({
      payload: {
        otp,
        token,
      },
    });

    if (!isSuccess) {
      setIsLoading2(false);
      return;
    }

    setVisible(false);
    setIsLoading2(false);
    setLoading(true);

    const promises = [];
    const updateUserPromise = userActions.updateArtistAction({
      payload: {
        first_name: get(formValues, 'first_name', ''),
        last_name: get(formValues, 'last_name', ''),
        username: get(formValues, 'username', ''),
        national_identity_no: Number(get(formValues, 'national_identity_no', '')),
        stage_name: get(formValues, 'stage_name', ''),
        description: get(formValues, 'description', ''),
        birthday: get(formValues, 'birthday'),
        country: get(formValues, 'country.value', ''),
      },
      id: artist.id,
    });
    promises.push(updateUserPromise);

    if (formValues.profile_img && typeof formValues.profile_img !== 'string') {
      const uploadAvatarPromise = userActions.uploadAvtarAction(formValues.profile_img, artist.id);
      promises.push(uploadAvatarPromise);
    }

    // Execute all promises and wait for them to settle
    const results = await Promise.allSettled(promises);

    // Extracting specific results if needed
    const [updateArtistResult] = results;

    if (updateArtistResult.status === 'fulfilled') {
      setArtist(get(updateArtistResult, 'value.data', {}));
    }

    if (updateArtistResult.status === 'fulfilled') {
      navigate(routesConstants.ROUTES.BANK_INFORMATION);
    }
    setLoading(false);
  };

  const handleChange =
    (id: string | object) => (value?: string | object | boolean | File | Date) => {
      if (typeof id !== 'object') {
        setFormValues({
          ...formValues,
          [id]: value,
        });
        setErr({
          ...err,
          [id]: '',
        });
      } else {
        setFormValues({
          ...formValues,
          ...id,
        });
        setErr({
          ...err,
          ...id,
        });
      }
    };

  const handleNextStep = async () => {
    const error = {
      first_name: authHelpers.validateFirstName(get(formValues, 'first_name')),
      last_name: authHelpers.validateLastName(get(formValues, 'last_name')),
      national_identity_no: authHelpers.validateNationalNo(
        get(formValues, 'national_identity_no', ''),
      ),

      phone: authHelpers.validatePhone(get(formValues, 'phone.number')),
      // stage_name: authHelpers.validateStageName(get(formValues, 'stage_name')),
      // description: authHelpers.validateDescription(get(formValues, 'description')),
      birthday: authHelpers.validateDate(get(formValues, 'birthday')),
      country: authHelpers.validateCountry(get(formValues, 'country.value', '')),
      username: authHelpers.validateUsername(get(formValues, 'username')),
    };

    if (!generalHelpers.isEmptyErrors(error)) {
      setErr({
        ...err,
        ...error,
      });
      return;
    }
    setLoading(true);

    // write api call here
    const data = await userActions.sendOtpAction({
      payload: {
        phone_no: formValues.phone.number,
        phone_no_code: formValues.phone.code,
      },
    });
    if (data.success) {
      setToken(data.data.token);
    }
    setLoading(false);
    setVisible(true);
  };

  return visible ? (
    <OtpScreen
      isLoading={isLoading2}
      phoneNumber={formValues.phone.number}
      visible={visible}
      handleVerify={handleSubmit}
      handleResend={handleNextStep}
      setVisible={() => {
        setVisible(false);
        // navigate(routesConstants.ROUTES.BANK_INFORMATION);
      }}
      handleSubmit={handleSubmit}
    />
  ) : (
    <div className={styles.PersonalInfo}>
      <div className={styles.logo}>
        <img src={logo} alt="logo" className={styles.logoImg} />
      </div>

      <div className={styles.box}>
        <h1 className={styles.title}>Personals Information</h1>
        <div className={styles.imagePicker}>
          <ImagePicker profileImgUrl={formValues.profile_img_url || ''} onChange={handleChange} />
        </div>
        <div className={styles.name}>
          <Input
            containerClassName={styles.input1}
            placeholder="First Name"
            value={formValues.first_name}
            onChange={handleChange('first_name')}
            errors={err}
            id="first_name"
          />
          <Input
            containerClassName={styles.input2}
            placeholder="Last Name"
            value={formValues.last_name}
            onChange={handleChange('last_name')}
            errors={err}
            id="last_name"
          />
        </div>
        <Input
          type="string"
          width="100%"
          placeholder="Username"
          value={formValues.username}
          onChange={handleChange('username')}
          errors={err}
          id="username"
        />
        <DatePickerInput
          placeholder="Birthdate"
          setDate={handleChange('birthday')}
          date={formValues.birthday}
          width="476px"
          error={err.birthday}
          id="birthday"
          // onClick={() => {
          //   setDatePickerVisible(true);
          // }}
        />
        <Dropdown
          className={styles.dropDown}
          placeholder="Country"
          options={generalConstants.countries}
          value={formValues.country}
          menuClassName={styles.menuClassName}
          errors={err}
          id="country"
          onMenuItemClick={handleChange('country')}
        />
        <Input
          type="number"
          width="100%"
          placeholder="National Identity No."
          value={formValues.national_identity_no}
          onChange={handleChange('national_identity_no')}
          maxLength={9}
          errors={err}
          id="national_identity_no"
        />
        {/* <Input
          type="number"
          width="100%"
          placeholder="Phone No."
          maxLength={9}
          value={formValues.phone_no}
          onChange={handleChange('phone_no')}
          errors={err}
          id="phone_no"
        /> */}
        <DropdownWithInput error={err.phone} onChange={handleChange} phone={formValues.phone} />
        <Input
          width="100%"
          placeholder="Stage Name (Optional)"
          value={formValues.stage_name}
          onChange={handleChange('stage_name')}
          errors={err}
          id="stage_name"
        />
        <Textarea
          className={styles.input6}
          placeHolder="Description (Optional)"
          value={formValues.description}
          onChange={handleChange('description')}
          error={err.description}
          onEnterClick={handleNextStep}
        />
        <Button className={styles.btn} onClick={handleNextStep} isLoading={loading} type="Primary">
          Next Step
        </Button>
      </div>
    </div>
  );
};

export default PersonalInfo;
